import { useEffect, useRef, useState } from 'react'

/**
 * Custom React hook that tracks whether a referenced DOM element is currently visible in the viewport,
 * and whether it has ever been visible.
 *
 * @template T - The type of the HTMLElement to observe.
 * @param options - Optional Intersection Observer options.
 * @returns
 *   An object containing:
 *   - `ref`: A React ref to be attached to the target element.
 *   - `isInViewport`: A boolean indicating if the element is currently in the viewport.
 *   - `hasBeenInViewport`: A boolean indicating if the element has ever been in the viewport.
 *
 * @example
 * ```tsx
 * const { ref, isInViewport, hasBeenInViewport } = useInViewport<HTMLDivElement>();
 * return <div ref={ref}>Hello</div>;
 * ```
 */
export const useInViewport = <T extends HTMLElement>(
  options: IntersectionObserverInit = {},
) => {
  const ref = useRef<T>(null)
  const [isInViewport, setIsInViewport] = useState(false)
  const [hasBeenInViewport, setHasBeenInViewport] = useState(false)

  useEffect(() => {
    if (!ref.current) return

    const observer = new window.IntersectionObserver(([entry]) => {
      setIsInViewport(entry.isIntersecting)
      if (entry.isIntersecting) {
        setHasBeenInViewport(true)
      }
    }, options)
    observer.observe(ref.current)

    return () => observer.disconnect()
  }, [])

  return { ref, isInViewport, hasBeenInViewport }
}
